import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import { getRefreshedTokens } from "../services/auth.service";
import { Auth } from "./auth";
import { LOCAL_STORAGE_KEYS } from "../common/constants";
import { isNull } from "underscore";
import { getRefreshedTokensAndUpdateThePreviousTokens } from "../common/utils";
import { logout } from "../common/common";
import axiosRetry from "axios-retry";

const client = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_API_HOST,
  headers: {
    "Content-Type": "application/json",
  },
});

// Helper function to safely combine URL paths
const combineUrls = (baseUrl: string | undefined, path: string | undefined): string => {
  if (!baseUrl || !path) {
    throw new Error("Both REACT_APP_BACKEND_API_HOST and REACT_APP_BACKEND_ARCHIVAL_API_HOST must be defined");
  }
  const normalizedBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${normalizedBase}${normalizedPath}`;
};

export const archivalClient = axios.create({
  baseURL: combineUrls(process.env.REACT_APP_BACKEND_API_HOST, process.env.REACT_APP_BACKEND_ARCHIVAL_API_HOST),
  headers: {
    "Content-Type": "application/json",
  },
});

export const client2 = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_API_HOST,
  headers: {
    "Content-Type": "application/json",
  },
});

export const adkClient = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_ADK_API_HOST,
  headers: {
    "Content-Type": "application/json",
    Authorization: `Bearer ${Auth.getInstance().getGoogleAccessToken() || ""}`,
  },
});

const clients = [client, client2];

// Proactive silent refresh: called by the Auth class timer SECONDS_TO_REFRESH_TOKEN
// seconds before the access token expires. If it succeeds, the timer is rescheduled
// for the new token. If it fails we let the normal reactive 401 path handle it.
export async function silentRefresh(): Promise<void> {
  // Enforce 24-hour maximum session — force re-login if exceeded
  const loginTimestamp = localStorage.getItem('loginTimestamp');
  if (loginTimestamp) {
    const sessionAgeMs = Date.now() - new Date(loginTimestamp).getTime();
    if (sessionAgeMs > 24 * 60 * 60 * 1000) {
      logout('SESSION_EXPIRED');
      return;
    }
  }

  const auth = Auth.getInstance();
  if (auth.isRefreshing) return; // an explicit refresh is already running
  const refreshToken = auth.getRefreshToken() || "";
  if (!refreshToken) return;
  auth.isRefreshing = true;
  try {
    const { data } = await getRefreshedTokens({ refresh: refreshToken });
    const { access: newAccessToken, refresh: newRefreshToken } = data;
    auth.saveAccessToken(newAccessToken);
    auth.saveRefreshToken(newRefreshToken);
    auth.onRefreshed(newAccessToken);
    // Broadcast new tokens to all other open tabs so they don't use the
    // now-blacklisted old refresh token and get logged out.
    auth.broadcastTokenUpdate(newAccessToken, newRefreshToken);
    client.defaults.headers.common["Authorization"] = `Bearer ${newAccessToken}`;
    // Reschedule for the next expiry cycle.
    auth.scheduleProactiveRefresh(newAccessToken, silentRefresh);
    // Refresh Google tokens independently — best effort.
    getRefreshedTokensAndUpdateThePreviousTokens().catch(() => {});
  } catch {
    // Drain any subscribers that queued while this proactive refresh was
    // in-flight (e.g. a real request that 401'd during this window) so
    // they are released rather than hanging indefinitely.
    auth.onRefreshed(null);
  } finally {
    auth.isRefreshing = false;
  }
}

// Start cross-tab token synchronization so all open tabs immediately
// receive the latest tokens whenever any one tab refreshes.
Auth.getInstance().subscribeToTokenUpdates(silentRefresh);

const nonProtectedRoutes = ["login", "/auth/get_gauth_token_from_auth_code/"];
const errorProneRoutes = ["/auth/get_gauth_token_from_refresh_token/"];
// The Django JWT refresh endpoint itself. It's called via client2 (see
// auth.service.ts), which carries the same response interceptor below — so
// without this guard, a 401 on this exact call would re-enter the generic
// 401 branch and fire a second, redundant /token/refresh/ call before
// finally giving up.
const tokenRefreshRoute = "/token/refresh/";
// Only the two tokens that can actually be refreshed are required.
// GOOGLE_AUTH_TOKEN is a one-time OAuth code never re-issued after initial
// login; including it caused spurious logouts whenever storage was cleared.
const mustHaveLocalStorageKeys = [
  LOCAL_STORAGE_KEYS.ACCESS_TOKEN,
  LOCAL_STORAGE_KEYS.REFRESH_TOKEN,
];
const axiosResponseErrorCodes = [AxiosError.ERR_BAD_REQUEST];

// Apply axios-retry to all clients
clients.forEach((client) => {
  axiosRetry(client, {
    retries: 2,
    retryDelay: axiosRetry.exponentialDelay,
    retryCondition: (error: any) =>
      axiosRetry.isNetworkOrIdempotentRequestError(error),
    onMaxRetryTimesExceeded: (err: any) => {
      if (err?.status >= 401 && err?.status < 500) {
        logout('TOKEN_EXPIRED');
      }
      return Promise.reject(err);
    },
  });
});

let abortController = new AbortController();

// Function to abort all requests
const abortAllRequests = () => {
  abortController.abort();
  // Create a new AbortController for future requests
  abortController = new AbortController();
};

clients.forEach((client) => {
  // Add a request interceptor
  client.interceptors.request.use(
    function (config: InternalAxiosRequestConfig) {
      // Check if there is an internet connection
      if (!navigator.onLine) {
        abortAllRequests();
        return Promise.reject(
          new Error("No internet connection. Request aborted.")
        );
      }

      // Set the AbortController's signal to the request config
      config.signal = abortController.signal;

      const auth = Auth.getInstance();
      const route = config.url!;
      const loginRoute = route?.split("/");
      const isLoginRoute =
        Array.isArray(loginRoute) &&
        loginRoute?.length > 1 &&
        nonProtectedRoutes.includes(loginRoute[1]);

      const isNonProtectedRoute =
        isLoginRoute || nonProtectedRoutes.includes(route);

      // if a route is non protected route, don't send Authorization header
      if (isNonProtectedRoute) return config;

      const isDataMissingFromLocalStorage = mustHaveLocalStorageKeys.some(
        (key) => isNull(localStorage.getItem(key))
      );

      // if some local storage keys go missing, logout of the application
      // and reject the request outright — letting it proceed unauthenticated
      // (as before) just fires a doomed request while logout() is still
      // in-flight.
      if (isDataMissingFromLocalStorage) {
        logout('TOKEN_EXPIRED');
        return Promise.reject(
          new Error("Session expired — required auth data missing from local storage.")
        );
      }

      const accessToken = auth.getAccessToken() || "";
      if (accessToken) config.headers.Authorization = `Bearer ${accessToken}`;

      return config;
    },
    function (error) {
      return Promise.reject(error);
    }
  );

  // Response interceptor
  client.interceptors.response.use(
    function (response) {
      return response;
    },
    async function (error) {
      const originalRequest = error.config;
      const auth = Auth.getInstance();
      const refreshToken = auth.getRefreshToken() || "";

      if (axios.isCancel(error)) {
        return Promise.reject(error);
      }

      // Immediate logout when the Google OAuth refresh endpoint itself 401s.
      if (
        error?.response?.status === 401 &&
        axiosResponseErrorCodes.includes(error?.code) &&
        errorProneRoutes.includes(error?.config?.url)
      ) {
        originalRequest._retry = true;
        logout('TOKEN_EXPIRED');
        return Promise.reject(error);
      }

      // The refresh call itself came back 401 (refresh token dead/blacklisted).
      // Retrying it here would just recurse into another /token/refresh/ call
      // via the generic branch below — go straight to logout instead.
      if (
        error.response &&
        error.response.status === 401 &&
        originalRequest?.url === tokenRefreshRoute
      ) {
        originalRequest._retry = true;
        auth.onRefreshed(null);
        logout('TOKEN_EXPIRED');
        return Promise.reject(error);
      }

      if (
        error.response &&
        error.response.status === 401 &&
        !originalRequest._retry
      ) {
        originalRequest._retry = true;

        // If a refresh is already in-flight, queue this request instead of
        // firing a duplicate /token/refresh/ call. Each queued request will
        // be retried (or rejected) once the in-flight refresh resolves.
        if (auth.isRefreshing) {
          return new Promise((resolve, reject) => {
            auth.subscribeTokenRefresh((newToken: string | null) => {
              if (newToken) {
                originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
                resolve(client(originalRequest));
              } else {
                reject(error);
              }
            });
          });
        }

        auth.isRefreshing = true;
        try {
          const response = await getRefreshedTokens({ refresh: refreshToken });
          const { access: newAccessToken, refresh: newRefreshToken } =
            response.data;

          auth.saveAccessToken(newAccessToken);
          auth.saveRefreshToken(newRefreshToken);

          // Notify all queued requests that a new token is available.
          auth.onRefreshed(newAccessToken);
          // Broadcast to sibling tabs so they don't use the old blacklisted token.
          auth.broadcastTokenUpdate(newAccessToken, newRefreshToken);

          // Reschedule proactive refresh for the new token's expiry.
          auth.scheduleProactiveRefresh(newAccessToken, silentRefresh);

          // Update the Google OAuth tokens independently (best-effort).
          // Failure here must NOT revoke a valid Django session.
          getRefreshedTokensAndUpdateThePreviousTokens().catch(() => {});

          client.defaults.headers.common[
            "Authorization"
          ] = `Bearer ${newAccessToken}`;
          return client(originalRequest);
        } catch (refreshError) {
          // Another tab may have already rotated the tokens via BroadcastChannel
          // while our refresh was in-flight. Check for a valid token before giving up.
          const currentToken = auth.getAccessToken();
          if (currentToken && auth.isValid(currentToken)) {
            // Sibling tab broadcast a fresh token — notify queued requests so they
            // can retry, then replay the original request with the new token.
            auth.onRefreshed(currentToken);
            originalRequest.headers["Authorization"] = `Bearer ${currentToken}`;
            return client(originalRequest);
          }
          auth.onRefreshed(null); // signal failure to all queued requests
          logout('TOKEN_EXPIRED');
          return Promise.reject(refreshError);
        } finally {
          auth.isRefreshing = false;
        }
      }
      return Promise.reject(error);
    }
  );
});

// Listen for online/offline events
window.addEventListener("offline", abortAllRequests);

export default client;
